import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@/utils/logger/logger';

import { GDT_PATIENT_RECEIVED_EVENT, type GdtPatientData } from '../types';

/**
 * Hook state for GDT patient context
 */
export interface GdtPatientState {
  /** Current patient data from GDT launch */
  patient: GdtPatientData | null;
  /** Whether the app was launched from a PVS */
  isGdtLaunch: boolean;
  /** Clear the current patient context */
  clearPatient: () => void;
}

/**
 * React hook for receiving GDT patient context
 *
 * Listens for the `gdt-patient-received` event emitted when the app
 * is launched from a German PVS with `/gdt <filepath>` argument.
 *
 * @param onPatientReceived - Optional callback when patient data is received
 * @returns GDT patient state
 *
 * @example
 * ```tsx
 * function PatientBanner() {
 *   const { patient, isGdtLaunch, clearPatient } = useGdtPatient();
 *
 *   if (!patient) return null;
 *
 *   return (
 *     <div className="patient-banner">
 *       <span>{patient.firstName} {patient.lastName}</span>
 *       <span>DOB: {patient.dateOfBirth}</span>
 *       <button onClick={clearPatient}>Clear</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useGdtPatient(
  onPatientReceived?: (patient: GdtPatientData) => void
): GdtPatientState {
  const [patient, setPatient] = useState<GdtPatientData | null>(null);
  const [isGdtLaunch, setIsGdtLaunch] = useState(false);

  // Use ref to avoid re-subscribing when callback changes
  const callbackRef = useRef(onPatientReceived);
  callbackRef.current = onPatientReceived;

  useEffect(() => {
    let unlisten: UnlistenFn | null = null;
    let isMounted = true;

    const setupListener = async () => {
      try {
        const unlistenFn = await listen<GdtPatientData>(
          GDT_PATIENT_RECEIVED_EVENT,
          (event) => {
            // Only update state if component is still mounted
            if (!isMounted) return;

            const patientData = event.payload;
            setPatient(patientData);
            setIsGdtLaunch(true);

            if (callbackRef.current) {
              callbackRef.current(patientData);
            }
          }
        );

        // If component unmounted while we were awaiting, cleanup immediately
        if (!isMounted) {
          unlistenFn();
          return;
        }

        unlisten = unlistenFn;
      } catch {
        // Log error but don't crash - GDT is optional functionality
        logger.error('[GDT] Failed to set up patient event listener');
      }
    };

    void setupListener();

    return () => {
      isMounted = false;
      if (unlisten) {
        unlisten();
      }
    };
  }, []);

  const clearPatient = useCallback(() => {
    setPatient(null);
    setIsGdtLaunch(false);
  }, []);

  return {
    patient,
    isGdtLaunch,
    clearPatient,
  };
}
